import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCanAccess, useNotify, useTranslate } from "react-admin";
import { v4 as uuidv4 } from "uuid";

import { ResourceName } from "../../constants/resources";
import {
  CancelLogisticsShippingLabelDocument,
  type CancelLogisticsShippingLabelMutation,
  type CancelLogisticsShippingLabelMutationVariables,
  CreateLogisticsShippingLabelDocument,
  type CreateLogisticsShippingLabelMutation,
  type CreateLogisticsShippingLabelMutationVariables,
  LogisticsOrderCommandState,
  LogisticsOrderCommandStatusDocument,
  type LogisticsOrderCommandStatusQuery,
  type LogisticsOrderCommandStatusQueryVariables,
  type LogisticsShippingLabelFieldsFragment,
  LogisticsShippingLabelOutcome,
  LogisticsShippingLabelPilotOutcome,
  LogisticsShippingLabelStateDocument,
  type LogisticsShippingLabelStateQuery,
  type LogisticsShippingLabelStateQueryVariables,
} from "../../generated/graphql";
import { CrmCard } from "../crm/CrmCard";
import { useShippingLabelPdf } from "./useShippingLabelPdf";

const CREATE_COMMAND = "create_shipping_label";
const CANCEL_COMMAND = "cancel_shipping_label";
const POLL_INTERVAL_MS = 2000;
type LabelRequest = Readonly<
  | { command: typeof CREATE_COMMAND; orderId: string; requestId: string; returnLabel: boolean }
  | { command: typeof CANCEL_COMMAND; orderId: string; requestId: string; labelId: string }
>;
type Result = CreateLogisticsShippingLabelMutation["createLogisticsShippingLabel"];

const PdfLinks = ({ label }: { label: LogisticsShippingLabelFieldsFragment }) => {
  const translate = useTranslate();
  const outbound = useShippingLabelPdf(label.labelPdf);
  const returned = useShippingLabelPdf(label.returnLabelPdf);
  return (
    <Stack spacing={1}>
      {label.cancelledAt && <Alert severity="warning">{translate("logisticsShippingLabel.cancelledHistory")}</Alert>}
      <Typography variant="body2">
        {translate("logisticsShippingLabel.outboundTracking", { number: label.shipmentNumber })}
      </Typography>
      {label.returnShipmentNumber && (
        <Typography variant="body2">
          {translate("logisticsShippingLabel.returnTracking", { number: label.returnShipmentNumber })}
        </Typography>
      )}
      {outbound.url && (
        <Stack direction="row" spacing={2}>
          <Link href={outbound.url} target="_blank" rel="noopener noreferrer">
            {translate("logisticsShippingLabel.openOutbound")}
          </Link>
          <Link href={outbound.url} download={`shipping-label-${label.id}.pdf`}>
            {translate("logisticsShippingLabel.downloadOutbound")}
          </Link>
        </Stack>
      )}
      {returned.url && (
        <Stack direction="row" spacing={2}>
          <Link href={returned.url} target="_blank" rel="noopener noreferrer">
            {translate("logisticsShippingLabel.openReturn")}
          </Link>
          <Link href={returned.url} download={`return-label-${label.id}.pdf`}>
            {translate("logisticsShippingLabel.downloadReturn")}
          </Link>
        </Stack>
      )}
      {(outbound.error || returned.error) && (
        <Alert severity="warning">{translate("logisticsShippingLabel.pdfError")}</Alert>
      )}
    </Stack>
  );
};

export const LogisticsShippingLabelCard = ({
  salesforceId,
  onSynced,
}: {
  salesforceId: string;
  onSynced: () => unknown;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const client = useApolloClient();
  const { canAccess } = useCanAccess({ action: "list", resource: ResourceName.LogisticsInventory });
  const query = useQuery<LogisticsShippingLabelStateQuery, LogisticsShippingLabelStateQueryVariables>(
    LogisticsShippingLabelStateDocument,
    {
      variables: { salesforceId },
      skip: canAccess !== true,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );
  const [createLabel, mutation] = useMutation<
    CreateLogisticsShippingLabelMutation,
    CreateLogisticsShippingLabelMutationVariables
  >(CreateLogisticsShippingLabelDocument);
  const [cancelLabel, cancelMutation] = useMutation<
    CancelLogisticsShippingLabelMutation,
    CancelLogisticsShippingLabelMutationVariables
  >(CancelLogisticsShippingLabelDocument);
  const { startPolling, stopPolling } = query;
  const [result, setResult] = useState<Result | null>(null);
  const [lastLabel, setLastLabel] = useState<LogisticsShippingLabelFieldsFragment | null>(null);
  const [remembered, setRemembered] = useState<LabelRequest | null>(null);
  const [resultCommand, setResultCommand] = useState<typeof CREATE_COMMAND | typeof CANCEL_COMMAND>(CREATE_COMMAND);
  const [returnLabel, setReturnLabel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transportLost, setTransportLost] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [statusRefreshError, setStatusRefreshError] = useState(false);
  const initialized = useRef(false);
  const inFlight = useRef(false);
  const handledSynced = useRef(new Set<string>());
  const refreshedCommands = useRef(new Set<string>());
  const state = query.data?.logisticsShippingLabelState;
  const readAttempt = state?.attempt;
  const localPilot = result?.pilot;
  const ownObserved = readAttempt?.requestId && readAttempt.requestId === localPilot?.requestId;
  const observedTerminal =
    readAttempt &&
    readAttempt.outcome !== LogisticsShippingLabelPilotOutcome.InProgress &&
    readAttempt.outcome !== LogisticsShippingLabelPilotOutcome.Unknown;
  const localTerminal =
    localPilot &&
    localPilot.outcome !== LogisticsShippingLabelPilotOutcome.InProgress &&
    localPilot.outcome !== LogisticsShippingLabelPilotOutcome.Unknown;
  const phase =
    result && !localPilot
      ? null
      : localPilot &&
          !(
            ownObserved &&
            (localPilot.outcome === LogisticsShippingLabelPilotOutcome.InProgress ||
              (localPilot.outcome === LogisticsShippingLabelPilotOutcome.Unknown && observedTerminal))
          )
        ? { ...localPilot, command: resultCommand, returnLabel }
        : readAttempt;
  const label = result?.label ?? lastLabel ?? state?.label;
  const unknown =
    phase?.outcome === LogisticsShippingLabelPilotOutcome.Unknown ||
    (readAttempt?.outcome === LogisticsShippingLabelPilotOutcome.Unknown && !(ownObserved && localTerminal)) ||
    (!localPilot &&
      (result?.outcome === LogisticsShippingLabelOutcome.Unknown ||
        result?.outcome === LogisticsShippingLabelOutcome.Failed));
  const pending = phase?.outcome === LogisticsShippingLabelPilotOutcome.InProgress;
  const anotherPending =
    readAttempt?.outcome === LogisticsShippingLabelPilotOutcome.InProgress &&
    !(ownObserved && localTerminal) &&
    (!readAttempt.requestId ||
      (remembered !== null && remembered.requestId !== readAttempt.requestId) ||
      (localPilot && localPilot.requestId !== readAttempt.requestId));
  const recovered: LabelRequest | null =
    pending && state && typeof phase?.requestId === "string"
      ? phase.command === CREATE_COMMAND && typeof phase.returnLabel === "boolean"
        ? {
            command: CREATE_COMMAND,
            orderId: state.orderId,
            requestId: phase.requestId,
            returnLabel: phase.returnLabel,
          }
        : phase.command === CANCEL_COMMAND && "labelId" in phase && typeof phase.labelId === "string"
          ? { command: CANCEL_COMMAND, orderId: state.orderId, requestId: phase.requestId, labelId: phase.labelId }
          : null
      : null;
  const resume = remembered ?? recovered;
  const originalReturnFlag = result
    ? returnLabel
    : state?.attempt?.command === CREATE_COMMAND
      ? state.attempt.returnLabel
      : null;
  const displayedReturnFlag =
    (resume?.command === CREATE_COMMAND ? resume.returnLabel : null) ??
    (label && !label.cancelledAt ? (originalReturnFlag ?? Boolean(label.returnShipmentNumber)) : returnLabel);
  const rejected =
    phase?.command === CREATE_COMMAND &&
    (phase.outcome === LogisticsShippingLabelPilotOutcome.Refused ||
      phase.outcome === LogisticsShippingLabelPilotOutcome.Rejected);
  const cancelRejected =
    phase?.command === CANCEL_COMMAND &&
    (phase.outcome === LogisticsShippingLabelPilotOutcome.Refused ||
      phase.outcome === LogisticsShippingLabelPilotOutcome.Rejected);
  const offerCreate =
    resume?.command !== CANCEL_COMMAND && (!label || Boolean(label.cancelledAt) || rejected || Boolean(resume));
  const offerCancel =
    resume?.command !== CREATE_COMMAND &&
    (resume?.command === CANCEL_COMMAND || Boolean(label && (!label.cancelledAt || cancelRejected)));
  const enabled =
    canAccess === true &&
    state?.enabled === true &&
    !query.error &&
    !refreshError &&
    !busy &&
    !mutation.loading &&
    !cancelMutation.loading &&
    !unknown &&
    !anotherPending &&
    (!pending || resume !== null);

  useEffect(() => {
    if (
      state?.label &&
      ((!result && !remembered) || (readAttempt?.requestId && readAttempt.requestId === remembered?.requestId))
    )
      setLastLabel(state.label);
    if (state && !initialized.current) {
      initialized.current = true;
      if (state.attempt?.command === CREATE_COMMAND && typeof state.attempt.returnLabel === "boolean")
        setReturnLabel(state.attempt.returnLabel);
    }
  }, [state, result, remembered, readAttempt]);
  useEffect(() => {
    if (
      remembered &&
      readAttempt?.requestId === remembered.requestId &&
      readAttempt.outcome !== LogisticsShippingLabelPilotOutcome.InProgress &&
      readAttempt.outcome !== LogisticsShippingLabelPilotOutcome.Unknown
    ) {
      setRemembered(null);
      setTransportLost(false);
    }
  }, [remembered, readAttempt]);
  useEffect(() => {
    if (canAccess === true && !unknown && (pending || anotherPending)) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [canAccess, unknown, pending, anotherPending, startPolling, stopPolling]);

  const refreshStatus = useCallback(
    async (commandId?: string | null) => {
      try {
        const cached = client.readQuery<LogisticsOrderCommandStatusQuery, LogisticsOrderCommandStatusQueryVariables>({
          query: LogisticsOrderCommandStatusDocument,
          variables: { salesforceId },
        })?.logisticsOrderCommandStatus;
        const alreadyObserved =
          cached?.commandId === commandId &&
          (cached?.state === LogisticsOrderCommandState.Updating ||
            cached?.state === LogisticsOrderCommandState.Synced);
        const response = await client.query<
          LogisticsOrderCommandStatusQuery,
          LogisticsOrderCommandStatusQueryVariables
        >({ query: LogisticsOrderCommandStatusDocument, variables: { salesforceId }, fetchPolicy: "network-only" });
        setStatusRefreshError(false);
        if (
          commandId &&
          !alreadyObserved &&
          !handledSynced.current.has(commandId) &&
          response.data.logisticsOrderCommandStatus?.commandId === commandId &&
          response.data.logisticsOrderCommandStatus.state === LogisticsOrderCommandState.Synced
        ) {
          handledSynced.current.add(commandId);
          await onSynced();
        }
      } catch {
        setStatusRefreshError(true);
      }
    },
    [client, salesforceId, onSynced]
  );
  useEffect(() => {
    const commandId = phase?.salesforceCommandId;
    if (
      typeof commandId === "string" &&
      phase?.outcome === LogisticsShippingLabelPilotOutcome.Succeeded &&
      !refreshedCommands.current.has(commandId)
    ) {
      refreshedCommands.current.add(commandId);
      void refreshStatus(commandId);
    }
  }, [phase?.salesforceCommandId, phase?.outcome, refreshStatus]);
  const refresh = async () => {
    setRefreshError(false);
    try {
      const fresh = await query.refetch();
      if (fresh.data?.logisticsShippingLabelState?.label) setLastLabel(fresh.data.logisticsShippingLabelState.label);
      // A read failure never discards a known mutation result or its PDF.
      const observed = fresh.data?.logisticsShippingLabelState?.attempt;
      const expectedId = remembered?.requestId ?? localPilot?.requestId;
      const resolved =
        typeof expectedId === "string" &&
        observed?.requestId === expectedId &&
        observed?.outcome !== LogisticsShippingLabelPilotOutcome.InProgress &&
        observed?.outcome !== LogisticsShippingLabelPilotOutcome.Unknown;
      if (!unknown || resolved) setResult(null);
    } catch {
      setRefreshError(true);
    }
    void refreshStatus(phase?.salesforceCommandId);
  };
  const execute = async (command: typeof CREATE_COMMAND | typeof CANCEL_COMMAND) => {
    if (inFlight.current || !enabled || !state || (resume && resume.command !== command)) return;
    if (!resume && command === CANCEL_COMMAND && !label) return;
    const request: LabelRequest =
      resume ??
      (command === CREATE_COMMAND
        ? { command, orderId: state.orderId, requestId: uuidv4(), returnLabel: displayedReturnFlag }
        : { command, orderId: state.orderId, requestId: uuidv4(), labelId: label!.id });
    inFlight.current = true;
    setBusy(true);
    setRemembered(request);
    setResult(null);
    try {
      const outcome =
        request.command === CREATE_COMMAND
          ? (
              await createLabel({
                variables: {
                  input: {
                    orderId: request.orderId,
                    requestId: request.requestId,
                    returnLabel: request.returnLabel,
                  },
                },
              })
            ).data?.createLogisticsShippingLabel
          : (
              await cancelLabel({
                variables: {
                  input: {
                    orderId: request.orderId,
                    requestId: request.requestId,
                    labelId: request.labelId,
                  },
                },
              })
            ).data?.cancelLogisticsShippingLabel;
      if (!outcome) throw new Error("Missing label response");
      setResult(outcome);
      setResultCommand(request.command);
      if (outcome.label) setLastLabel(outcome.label);
      if (request.command === CREATE_COMMAND) setReturnLabel(request.returnLabel);
      setTransportLost(false);
      if (
        outcome.pilot?.outcome !== LogisticsShippingLabelPilotOutcome.InProgress &&
        outcome.pilot?.outcome !== LogisticsShippingLabelPilotOutcome.Unknown
      )
        setRemembered(null);
      const commandId = outcome.pilot?.salesforceCommandId;
      if (typeof commandId === "string") refreshedCommands.current.add(commandId);
      void refreshStatus(commandId);
    } catch {
      setTransportLost(true);
      notify("logisticsShippingLabel.transportLost", { type: "warning" });
      void query.refetch().catch(() => setRefreshError(true));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (canAccess !== true) return null;
  const refusal = localPilot?.refusal ?? result?.refusal ?? phase?.refusal ?? state?.refusal;
  const reason = (code: string) =>
    translate(`logisticsShippingLabel.refusals.${code}`, {
      _: translate(`logisticsCommandStatus.refusals.${code}`, {
        _: translate("logisticsShippingLabel.refusalFallback"),
      }),
    });
  const messages = result
    ? Array.from(
        new Set([
          ...result.messages.map(message => message.message),
          ...((phase === readAttempt ? phase?.messages : localPilot?.messages) ?? []),
        ])
      )
    : (phase?.messages ?? []);
  return (
    <Box sx={{ mb: "12px" }}>
      <CrmCard title={translate("logisticsShippingLabel.title")} testId="logistics-shipping-label-card">
        <Stack spacing={1.5}>
          {query.loading && !state && <Typography>{translate("logisticsShippingLabel.loading")}</Typography>}
          {(query.error || refreshError) && (
            <Alert severity="warning">{translate("logisticsShippingLabel.loadError")}</Alert>
          )}
          {!query.loading && !query.error && !state && (
            <Alert severity="info">{translate("logisticsShippingLabel.noOrder")}</Alert>
          )}
          {state && !state.enabled && <Alert severity="info">{translate("logisticsShippingLabel.disabled")}</Alert>}
          {result && !(result.outcome === LogisticsShippingLabelOutcome.Unknown && ownObserved && observedTerminal) && (
            <Typography>{translate(`logisticsShippingLabel.outcomes.${result.outcome}`)}</Typography>
          )}
          {phase && (
            <Typography>
              {translate(
                phase.salesforceCommandId
                  ? "logisticsShippingLabel.salesforceStatus"
                  : "logisticsShippingLabel.pilotStatus"
              )}{" "}
              {translate(`logisticsShippingLabel.phases.${phase.outcome}`)}
            </Typography>
          )}
          {anotherPending && <Alert severity="info">{translate("logisticsShippingLabel.anotherPending")}</Alert>}
          {unknown && <Alert severity="warning">{translate("logisticsShippingLabel.unknown")}</Alert>}
          {transportLost && <Alert severity="warning">{translate("logisticsShippingLabel.transportLost")}</Alert>}
          {statusRefreshError && (
            <Alert severity="warning">{translate("logisticsShippingLabel.statusRefreshError")}</Alert>
          )}
          {refusal && <Alert severity="warning">{reason(refusal)}</Alert>}
          {result?.failure && (
            <Alert severity="warning">{translate(`logisticsShippingLabel.failures.${result.failure}`)}</Alert>
          )}
          {result?.missingAddressFields.map(field => (
            <Typography key={field}>{translate(`logisticsShippingLabel.address.${field}`)}</Typography>
          ))}
          {messages.map((message, index) => (
            <Box key={index} sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {message}
            </Box>
          ))}
          {label && <PdfLinks label={label} />}
          {label?.cancelledAt && (
            <Typography variant="body2">{translate("logisticsShippingLabel.cancelMarker")}</Typography>
          )}
          {offerCreate && !unknown && !anotherPending && (!pending || resume) && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={displayedReturnFlag}
                    disabled={!enabled || Boolean(resume) || Boolean(label && !label.cancelledAt)}
                    onChange={event => setReturnLabel(event.target.checked)}
                  />
                }
                label={translate("logisticsShippingLabel.returnLabel")}
              />
              <Button variant="outlined" disabled={!enabled} onClick={() => void execute(CREATE_COMMAND)}>
                {translate(
                  busy
                    ? "logisticsShippingLabel.creating"
                    : resume
                      ? "logisticsShippingLabel.continue"
                      : rejected
                        ? label && !label.cancelledAt
                          ? "logisticsShippingLabel.retry"
                          : "logisticsShippingLabel.retryCreate"
                        : "logisticsShippingLabel.create"
                )}
              </Button>
            </>
          )}
          {offerCancel && !unknown && !anotherPending && (!pending || resume) && (
            <Button variant="outlined" disabled={!enabled} onClick={() => void execute(CANCEL_COMMAND)}>
              {translate(
                busy
                  ? "logisticsShippingLabel.cancelling"
                  : resume
                    ? "logisticsShippingLabel.continue"
                    : label?.cancelledAt
                      ? "logisticsShippingLabel.retryTracking"
                      : "logisticsShippingLabel.cancel"
              )}
            </Button>
          )}
          <Button variant="text" disabled={busy} onClick={() => void refresh()}>
            {translate("logisticsShippingLabel.refresh")}
          </Button>
        </Stack>
      </CrmCard>
    </Box>
  );
};
